import { useState, useEffect, useRef, useMemo, type FormEvent } from 'react';
import { Plus, Monitor, Search, Upload, X, ChevronDown } from 'lucide-react';
import { machineApi, floorPlanApi } from '@/services/api';
import type { MachineSortBy } from '@/services/api';
import { useUIStore } from '@/store/uiStore';
import { Card, Button, Modal } from '@/components/common';
import { MachinePositionPicker } from '@/components/floorplan';
import { MachineGridCard } from '@/components/machines';
import type { Machine, GymFloorPlan } from '@/types';

const Machines = () => {
  const [machines, setMachines] = useState<Machine[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [floorPlans, setFloorPlans] = useState<GymFloorPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedFloorPlanId, setSelectedFloorPlanId] = useState('');
  const [sortBy, setSortBy] = useState<MachineSortBy>('usage');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingMachine, setEditingMachine] = useState<Machine | null>(null);
  const { showToast } = useUIStore();

  const [formName, setFormName] = useState('');
  const [formType, setFormType] = useState('');
  const [formLocation, setFormLocation] = useState('');
  const [formPhotoUrl, setFormPhotoUrl] = useState<string | null>(null);
  const [formFloorPlanId, setFormFloorPlanId] = useState<string | null>(null);
  const [formPosition, setFormPosition] = useState<{ x: number; y: number } | null>(null);
  const [formSaving, setFormSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadMachines = async () => {
    try {
      const params: {
        type?: string;
        search?: string;
        sortBy: MachineSortBy;
        floorPlanId?: string;
      } = {
        sortBy,
      };
      if (selectedType) params.type = selectedType;
      if (searchQuery) params.search = searchQuery;
      if (selectedFloorPlanId) params.floorPlanId = selectedFloorPlanId;

      const response = await machineApi.getAll(params);
      if (response.success && response.data) {
        setMachines(response.data.machines);
      }
    } catch (error) {
      console.error('Failed to load machines:', error);
      showToast('Hiba a gépek betöltésekor.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadTypes = async () => {
    try {
      const response = await machineApi.getTypes();
      if (response.success && response.data) {
        setTypes(response.data.types);
      }
    } catch (error) {
      console.error('Failed to load types:', error);
    }
  };

  const loadFloorPlans = async () => {
    try {
      const response = await floorPlanApi.getAll({ activeOnly: true });
      if (response.success && response.data) {
        setFloorPlans(response.data.floorPlans);
      }
    } catch (error) {
      console.error('Failed to load floor plans:', error);
    }
  };

  const activeFloorPlanIds = useMemo(
    () => new Set(floorPlans.map((fp) => fp.id)),
    [floorPlans]
  );

  useEffect(() => {
    loadMachines();
    loadTypes();
    loadFloorPlans();
  }, [selectedType, searchQuery, selectedFloorPlanId, sortBy]);

  const openModal = (machine?: Machine) => {
    if (machine) {
      setEditingMachine(machine);
      setFormName(machine.name);
      setFormType(machine.type);
      setFormLocation(machine.location || '');
      setFormPhotoUrl(machine.photoUrl || null);
      // Parse floor plan position
      if (machine.floorPlanPosition) {
        setFormFloorPlanId(machine.floorPlanPosition.floorPlanId || null);
        setFormPosition({ x: machine.floorPlanPosition.x, y: machine.floorPlanPosition.y });
      } else {
        setFormFloorPlanId(null);
        setFormPosition(null);
      }
    } else {
      setEditingMachine(null);
      setFormName('');
      setFormType('');
      setFormLocation('');
      setFormPhotoUrl(null);
      setFormFloorPlanId(null);
      setFormPosition(null);
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingMachine(null);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showToast('Csak képfájl tölthető fel!', 'error');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showToast('A kép maximum 5MB lehet!', 'error');
      return;
    }

    // For new machines, we need to create first then upload
    // For existing machines, we can upload directly
    if (!editingMachine) {
      // Store file for upload after creation
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormPhotoUrl(event.target?.result as string);
      };
      reader.readAsDataURL(file);
      // Store the file for later upload
      (window as any).__pendingMachineFile = file;
    } else {
      // Upload immediately for existing machines
      await uploadPhoto(editingMachine.id, file);
    }
  };

  const uploadPhoto = async (machineId: string, file: File) => {
    setUploading(true);
    try {
      // Upload photo through backend (avoids CORS issues with R2)
      const response = await machineApi.uploadPhoto(machineId, file);

      if (!response.success || !response.data) {
        throw new Error('Failed to upload photo');
      }

      setFormPhotoUrl(response.data.photoUrl);
      showToast('Kép sikeresen feltöltve!', 'success');
      loadMachines();
    } catch (error) {
      console.error('Upload failed:', error);
      showToast('Hiba a kép feltöltésekor.', 'error');
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async () => {
    if (editingMachine && editingMachine.photoUrl) {
      try {
        await machineApi.update(editingMachine.id, { photoUrl: null });
        setFormPhotoUrl(null);
        showToast('Kép eltávolítva!', 'success');
        loadMachines();
      } catch (error) {
        console.error('Failed to remove photo:', error);
        showToast('Hiba a kép eltávolításakor.', 'error');
      }
    } else {
      setFormPhotoUrl(null);
      (window as any).__pendingMachineFile = null;
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormSaving(true);

    // Build floor plan position object
    const floorPlanPosition = formFloorPlanId && formPosition
      ? { x: formPosition.x, y: formPosition.y, floorPlanId: formFloorPlanId }
      : null;

    try {
      if (editingMachine) {
        await machineApi.update(editingMachine.id, {
          name: formName,
          type: formType,
          location: formLocation || null,
          floorPlanPosition,
        });
        showToast('Gép sikeresen módosítva!', 'success');
      } else {
        // Create machine first
        const response = await machineApi.create({
          name: formName,
          type: formType,
          location: formLocation || null,
          floorPlanPosition,
        });

        // If we have a pending file, upload it now
        const pendingFile = (window as any).__pendingMachineFile;
        if (pendingFile && response.success && response.data) {
          await uploadPhoto(response.data.machine.id, pendingFile);
          (window as any).__pendingMachineFile = null;
        }

        showToast('Gép sikeresen hozzáadva!', 'success');
      }
      closeModal();
      loadMachines();
    } catch (error) {
      console.error('Failed to save machine:', error);
      showToast('Hiba történt a mentés során.', 'error');
    } finally {
      setFormSaving(false);
    }
  };

  const handleDelete = async (machine: Machine) => {
    if (!confirm(`Biztosan törlöd a "${machine.name}" gépet?`)) return;

    try {
      await machineApi.delete(machine.id);
      showToast('Gép sikeresen törölve!', 'success');
      loadMachines();
    } catch (error) {
      console.error('Failed to delete machine:', error);
      showToast('Hiba történt a törlés során.', 'error');
    }
  };

  // Get floor plan name for a machine
  const getFloorPlanName = (machine: Machine) => {
    if (!machine.floorPlanPosition?.floorPlanId) return null;
    const fpId = machine.floorPlanPosition.floorPlanId;
    if (!activeFloorPlanIds.has(fpId)) return null;
    const fp = floorPlans.find((f) => f.id === fpId);
    return fp?.name || null;
  };

  const hasActiveFilters = Boolean(searchQuery || selectedType || selectedFloorPlanId);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Edzőgépek</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1 sm:mt-2 text-sm sm:text-base">A konditeremben használt gépek kezelése</p>
        </div>
        <Button size="sm" icon={<Plus className="w-4 h-4" />} onClick={() => openModal()} className="flex-shrink-0 self-start sm:self-auto">
          Új gép
        </Button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400"
            placeholder="Keresés..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="relative">
          <select
            className="w-full px-2 py-1.5 pr-8 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
          >
            <option value="">Minden izomcsoport</option>
            {types.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>
        <div className="relative">
          <select
            className="w-full px-2 py-1.5 pr-8 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            value={selectedFloorPlanId}
            onChange={(e) => setSelectedFloorPlanId(e.target.value)}
          >
            <option value="">Minden aktív alaprajz</option>
            {floorPlans.map((fp) => (
              <option key={fp.id} value={fp.id}>
                {fp.name}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>
        <div className="relative">
          <select
            className="w-full px-2 py-1.5 pr-8 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as MachineSortBy)}
          >
            <option value="usage">Rendezés: népszerűség</option>
            <option value="name">Rendezés: ABC</option>
            <option value="createdAt">Rendezés: rögzítés dátuma</option>
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {/* Machines Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : machines.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <Monitor className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              {hasActiveFilters ? 'Nincs találat' : 'Nincs még gép'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {hasActiveFilters
                ? 'Próbálj más szűrőket vagy keresési kifejezést.'
                : 'Add hozzá az első edzőgépet az „Új gép” gombbal.'}
            </p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {machines.map((machine) => (
            <MachineGridCard
              key={machine.id}
              machine={machine}
              floorPlanName={getFloorPlanName(machine)}
              onEdit={() => openModal(machine)}
              onDelete={() => handleDelete(machine)}
            />
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingMachine ? 'Gép szerkesztése' : 'Új gép hozzáadása'}
        size="xl"
        footer={
          <>
            <button
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              onClick={closeModal}
            >
              Mégse
            </button>
            <Button type="submit" form="machine-form" loading={formSaving}>
              Mentés
            </Button>
          </>
        }
      >
        <form id="machine-form" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left column - Basic info */}
            <div className="space-y-4">
              {/* Photo Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                  Gép képe
                </label>
                <div className="flex items-start gap-4">
                  {formPhotoUrl ? (
                    <div className="relative">
                      <img
                        src={formPhotoUrl}
                        alt="Preview"
                        className="w-24 h-24 object-cover rounded-lg border border-gray-300 dark:border-gray-600"
                      />
                      <button
                        type="button"
                        onClick={removePhoto}
                        className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="w-24 h-24 border-2 border-dashed border-gray-300 dark:border-gray-500 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                    >
                      {uploading ? (
                        <div className="w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <Upload className="w-6 h-6 text-gray-400 mb-1" />
                          <span className="text-xs text-gray-500 dark:text-gray-400">Feltöltés</span>
                        </>
                      )}
                    </div>
                  )}
                  <div className="flex-1 text-sm text-gray-500 dark:text-gray-400">
                    <p>JPG, PNG, WebP</p>
                    <p>Max 5MB</p>
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                  Gép neve <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="pl. Mellnyomó gép"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                  Izomcsoport <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none"
                    value={formType}
                    onChange={(e) => setFormType(e.target.value)}
                    required
                  >
                    <option value="">Válassz izomcsoportot...</option>
                    {types.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                  Helyszín (szöveges leírás)
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="pl. Földszint, jobb hátsó sarok"
                  value={formLocation}
                  onChange={(e) => setFormLocation(e.target.value)}
                />
              </div>
            </div>

            {/* Right column - Floor plan position */}
            <div>
              <MachinePositionPicker
                floorPlans={floorPlans}
                selectedFloorPlanId={formFloorPlanId}
                position={formPosition}
                onFloorPlanChange={setFormFloorPlanId}
                onPositionChange={setFormPosition}
              />
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Machines;
